import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApprovalEvent,
  ApproveJournalEntry,
  CreateJournalEntry,
  JEStatus,
  JournalEntry,
  JournalEntryApproval,
  JournalLine,
  RejectJournalEntry,
  ReturnJournalEntry,
  UpdateJournalEntry,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

import type { QueryClient } from '@tanstack/react-query';

// Cache key is keyed by companyId so switching company gives independent caches and
// invalidations don't bleed across DCC / TTOP / RCI (same convention as branches.ts).
const listKey = (companyId: string | undefined) => ['journal-entries', companyId] as const;

export interface JournalEntryFilters {
  // 'YYYYMM' — accounting period code
  period?: string;
  status?: JEStatus;
  // ISO date string YYYY-MM-DD
  dateFrom?: string;
  dateTo?: string;
}

// GET /journal-entries returns headers only. GET /journal-entries/:id returns
// header + lines + approvals. Surface the with-lines+approvals shape as a
// separate type so consumers (like the detail card) get typed access.
export interface JournalEntryWithApprovals extends JournalEntry {
  lines: JournalLine[];
  approvals: JournalEntryApproval[];
}

// Invalidate everything a JV mutation can affect. PREFIX keys only — TanStack
// prefix-matches, so the `['journal-entries', companyId]` key catches every
// filter/period permutation AND the per-entry detail + approval-events streams
// (`['journal-entries', companyId, id, 'approval-events']`) — so the AuditLog
// panel refreshes after an action without listing every sub-key explicitly.
function invalidateJv(qc: QueryClient, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['ledger', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

export function useJournalEntries(
  companyId: string | undefined,
  filters: JournalEntryFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.status) qs.set('status', filters.status);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  const search = qs.toString();
  return useQuery({
    queryKey: [...listKey(companyId), filters],
    queryFn: () =>
      api<JournalEntry[]>(`/companies/${companyId}/journal-entries${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useJournalEntry(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['journal-entries', companyId, id],
    queryFn: () => api<JournalEntryWithApprovals>(`/companies/${companyId}/journal-entries/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Full append-only audit history for one JV (Component 7). Distinct from the
// live approval rows on the detail (which are mutable chain state) — this is the
// immutable event stream rendered by the shared AuditLog panel.
export function useJournalApprovalEvents(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['journal-entries', companyId, id, 'approval-events'],
    queryFn: () =>
      api<ApprovalEvent[]>(`/companies/${companyId}/journal-entries/${id}/approval-events`),
    enabled: !!companyId && !!id,
  });
}

// POST uses `api()` directly (not `apiJson`) because we need a custom header
// (Idempotency-Key) on the request — `apiJson` doesn't accept extra headers.
// On success returns the persisted JV with lines included.
export function useCreateJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateJournalEntry; idempotencyKey?: string }) =>
      api<JournalEntryWithApprovals>(`/companies/${companyId}/journal-entries`, {
        method: 'POST',
        headers: {
          ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        },
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function useUpdateJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJournalEntry }) =>
      apiJson<UpdateJournalEntry, JournalEntryWithApprovals>(
        'PATCH',
        `/companies/${companyId}/journal-entries/${id}`,
        data,
      ),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function useSubmitJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, JournalEntryWithApprovals>(
        'POST',
        `/companies/${companyId}/journal-entries/${id}/submit`,
      ),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function useApproveJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ApproveJournalEntry) =>
      apiJson<ApproveJournalEntry, JournalEntryWithApprovals>(
        'POST',
        `/companies/${companyId}/journal-entries/${id}/approve`,
        body,
      ),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function useRejectJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & RejectJournalEntry) =>
      apiJson<RejectJournalEntry, JournalEntryWithApprovals>(
        'POST',
        `/companies/${companyId}/journal-entries/${id}/reject`,
        body,
      ),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function useReturnJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ReturnJournalEntry) =>
      apiJson<ReturnJournalEntry, JournalEntryWithApprovals>(
        'POST',
        `/companies/${companyId}/journal-entries/${id}/return`,
        body,
      ),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function usePostJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, JournalEntryWithApprovals>(
        'POST',
        `/companies/${companyId}/journal-entries/${id}/post`,
      ),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function useDeleteJournalEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>('DELETE', `/companies/${companyId}/journal-entries/${id}`),
    onSuccess: () => invalidateJv(qc, companyId),
  });
}

export function useSubmittableDocTypes(companyId: string | undefined) {
  return useQuery({
    queryKey: ['journal-entries', companyId, 'submittable-doctypes'],
    queryFn: () => api<string[]>(`/companies/${companyId}/journal-entries/submittable-doctypes`),
    enabled: !!companyId,
  });
}
